import { QueryClient } from '@tanstack/react-query';
import { sastDateKey } from '@/shared/utils/sastDate';

/**
 * Shared QueryClient for the portal (Phase 6).
 *
 * WHY: every data hook in the portal was a hand-rolled
 * `useState` + `useEffect` + `.then/.catch/.finally` block — the same ~40 lines
 * repeated across useServices, useDomains, useInvoices, useTickets and both
 * dashboards. Consequences that were actually biting:
 *
 *   - NO RETRY. A single transient 500 left a panel permanently broken until
 *     the next 60s tick. During the 2026-08-03 outage this turned a slow query
 *     into a blank dashboard.
 *   - NO CACHE. Every navigation refetched everything; the client dashboard
 *     fired four independent uncached queries on every mount.
 *   - NO DEDUPE. Two components needing invoices issued two requests.
 *   - NO STALE-WHILE-REVALIDATE. The hand-rolled 60s setInterval replaced good
 *     data with an error state on a single blip.
 *
 * This ALSO satisfies the owner's "hold state, only recompute when something
 * changes" requirement. The architect rejected a database-side watermark with
 * triggers (2026-08-03): every business table combined is ~6MB against a 224MB
 * buffer cache, so recomputation is single-digit milliseconds and money-path
 * triggers would be a bad trade. Caching belongs in the client, where it costs
 * the database nothing.
 */
export function createPortalQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Long enough to make navigation feel instant, short enough that an
        // operational dashboard is never meaningfully out of date.
        staleTime: 30_000,
        // Two retries with backoff. This alone would have masked the 2026-08-03
        // P0 while it was being fixed.
        retry: 2,
        // Staff leave the dashboard open on a second monitor; refresh when they
        // come back to it rather than serving whatever was true this morning.
        refetchOnWindowFocus: true,
      },
    },
  });
}

/**
 * Query-key helper for anything scoped to "today".
 *
 * THE TRAP (architect, 2026-08-03): a date rollover is itself a change. At SAST
 * midnight "today" becomes a different day even though no row changed, so a
 * cache keyed only on the query name keeps serving yesterday's numbers to a tab
 * that has been left open. Including the SAST date in the key makes the
 * rollover invalidate the entry for free.
 *
 * SAST, not UTC — the database runs in UTC and the business does not (see
 * public.sast_date and migration 20260803054500).
 */
export function todayScopedKey(...parts: readonly unknown[]): unknown[] {
  return [...parts, sastDateKey()];
}
